import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, MediaAsset } from '../api';
import { mediaEndpoints } from '../api';
import { Button } from '../ui';

/**
 * Choosing a picture (D-87).
 *
 * The same component wherever an image is needed — an event's poster, a
 * sponsor's logo, an image inside event content, a ticket design — so staff
 * learn it once, and so every one of those places picks from the same library
 * instead of re-uploading the same logo for the fourth time. Uploading is
 * offered here too, because the moment you need a picture is the moment you
 * have it in your hand; what is uploaded here is in the library for everyone
 * afterward.
 *
 * The file goes to our own authenticated endpoint. No R2 credential of any
 * kind reaches the browser.
 */

export function MediaPicker({
  open,
  onClose,
  onChoose,
  category,
  title = 'Choose an image',
}: {
  open: boolean;
  onClose: () => void;
  onChoose: (asset: MediaAsset) => void;
  /** Pre-filters the library and labels anything uploaded from here. */
  category?: string;
  title?: string;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(category ?? '');
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const assets = useQuery({
    queryKey: ['media', filter, query],
    queryFn: () => mediaEndpoints.list({ category: filter || null, q: query || null }),
    enabled: open,
  });
  const categories = useQuery({ queryKey: ['media-categories'], queryFn: mediaEndpoints.categories, enabled: open });

  const upload = useMutation({
    mutationFn: (file: File) => mediaEndpoints.upload(file, { category: filter || category || null, title: file.name }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['media'] });
      onChoose(result.asset);
      onClose();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const choose = useCallback(
    (asset: MediaAsset) => {
      onChoose(asset);
      onClose();
    },
    [onChoose, onClose],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex max-h-[80vh] w-full max-w-4xl flex-col rounded-lg border border-line bg-surface shadow-lg">
        <header className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <h2 className="text-base font-semibold text-ink-900">{title}</h2>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name"
            aria-label="Search the media library"
            className="ml-auto rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Category" className="rounded border border-line bg-surface px-2 py-1.5 text-sm">
            <option value="">Every category</option>
            {(categories.data?.categories ?? []).map((row) => (
              <option key={row.code} value={row.code}>
                {row.name}
              </option>
            ))}
          </select>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
          <Button variant="primary" disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </header>

        {error ? <p className="border-b border-line bg-bad-soft px-5 py-2 text-sm text-bad">{error}</p> : null}

        <div className="grid flex-1 grid-cols-2 gap-3 overflow-y-auto p-5 sm:grid-cols-4 lg:grid-cols-5">
          {assets.isPending ? <p className="text-sm text-ink-400">Loading…</p> : null}
          {assets.data?.assets.length === 0 ? (
            <p className="col-span-full text-sm text-ink-600">Nothing here yet. Upload the first one.</p>
          ) : null}
          {(assets.data?.assets ?? []).map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => choose(asset)}
              className="group rounded border border-line bg-page p-2 text-left hover:border-accent focus-visible:outline-2 focus-visible:outline-accent"
            >
              <img src={asset.url} alt={asset.alt_text ?? ''} className="h-24 w-full rounded object-contain" loading="lazy" />
              <p className="mt-2 truncate text-xs font-medium text-ink-900">{asset.title ?? asset.original_filename ?? 'Untitled'}</p>
              <p className="text-xs text-ink-400">
                {asset.width_px && asset.height_px ? `${asset.width_px}×${asset.height_px}` : asset.content_type.replace('image/', '')}
                {asset.usage_count > 0 ? ` · used ${asset.usage_count}×` : ''}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
